import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, CheckCircle, SpinnerGap } from "@phosphor-icons/react";
import { api, errMsg, img } from "@/lib/api";
import { FullLoader } from "@/lib/auth";
import { LogoMark } from "@/components/common";

const LABELS = ["", "Poor", "Okay", "Good", "Great", "Amazing"];

export default function Feedback() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get(`/public/feedback/${code}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e, "Feedback link not found"))); }, [code]);

  const submit = async () => {
    if (!rating) return setErr("Tap a star to rate your stay.");
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(`/public/feedback/${code}`, { rating, comment });
      setDone(r.data);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  if (!d && err) return <div className="grid min-h-screen place-items-center px-6 text-center" data-testid="feedback-not-found"><p className="font-display text-2xl">{err}</p></div>;
  if (!d) return <FullLoader />;
  const p = d.property;
  const thanks = done || d.submitted;

  return (
    <div data-theme={p.theme} className="min-h-screen" style={{ background: "var(--t-bg)", color: "var(--t-text)" }} data-testid="feedback-page">
      <div className="mx-auto max-w-md">
        <header className="relative h-48 overflow-hidden">
          {p.cover ? <img src={img(p.cover)} alt="" className="h-full w-full object-cover" /> : <div className="h-full" style={{ background: "var(--t-soft)" }} />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <h1 className="absolute bottom-4 left-5 right-5 font-display text-2xl font-medium text-white">{p.name}</h1>
        </header>
        <div className="p-5">
          {thanks ? (
            <div className="rise rounded-3xl border p-6 text-center" style={{ background: "var(--t-card)", borderColor: "var(--t-border)" }} data-testid="feedback-thanks">
              <CheckCircle size={44} weight="fill" className="mx-auto" style={{ color: "var(--t-primary)" }} />
              <p className="mt-3 font-display text-2xl">Asante, {d.guest}!</p>
              <p className="mt-1 text-sm" style={{ color: "var(--t-muted)" }}>Your feedback has been sent to {p.host_name || "your host"}.</p>
              {(done?.review_url || d.review_url) && (
                <a href={done?.review_url || d.review_url} target="_blank" rel="noreferrer" data-testid="feedback-google-review-button" className="mt-5 inline-flex h-12 items-center gap-2 rounded-full px-6 font-semibold" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>
                  <Star weight="fill" /> Also leave a Google review
                </a>
              )}
            </div>
          ) : (
            <div className="rounded-3xl border p-6" style={{ background: "var(--t-card)", borderColor: "var(--t-border)" }}>
              <p className="label-xs" style={{ color: "var(--t-primary)" }}>Feedback</p>
              <h2 className="mt-1 font-display text-2xl">How was your stay, {d.guest}?</h2>
              <div className="mt-5 flex justify-between" data-testid="feedback-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => { setRating(n); setErr(""); }} data-testid={`feedback-star-${n}`} className="transition-transform duration-150 active:scale-90" aria-label={`${n} stars`}>
                    <Star size={46} weight={n <= rating ? "fill" : "regular"} style={{ color: "var(--t-primary)" }} />
                  </button>
                ))}
              </div>
              <p className="mt-2 h-5 text-center text-sm font-semibold" style={{ color: "var(--t-primary)" }}>{LABELS[rating]}</p>
              <textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What did you love? What could be better?" data-testid="feedback-comment-input"
                className="mt-4 w-full rounded-xl border p-3.5 text-base outline-none focus:ring-2 focus:ring-[var(--t-primary)]" style={{ background: "var(--t-bg)", borderColor: "var(--t-border)", color: "var(--t-text)" }} />
              {err && <p className="mt-2 text-sm text-red-600" data-testid="feedback-error">{err}</p>}
              <button onClick={submit} disabled={busy} data-testid="feedback-submit-button" className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-full font-semibold disabled:opacity-60" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>
                {busy ? <SpinnerGap className="animate-spin" /> : "Send feedback"}
              </button>
            </div>
          )}
          <p className="flex items-center justify-center gap-1.5 pt-6 text-xs" style={{ color: "var(--t-muted)" }}>Powered by <LogoMark size={14} /> <b>hostkey.online</b></p>
        </div>
      </div>
    </div>
  );
}
